import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';

// PACKAGES
import { useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import PageName from '../../components/PageName';
import Button from '../../components/Button';
import CircleImage from '../../components/CircleImage';
import DisplayDateTime from '../../components/DisplayDateTime';
import ComplaintsProgress from './ComplaintsProgress/ComplaintsProgress';
import GlobalModal from '../../components/GlobalModal';
import ResolveModal from './Modals/ResolveModal';
import ReviewResolutionModal from './Modals/ReviewResolutionModal';
import ViewResolutionModal from './Modals/ViewResolutionModal';
import ReOpenModal from './Modals/ReOpenModal';
import WithdrawComplaintModal from './Modals/WithdrawComplaintModal';
import EditResolutionModal from './Modals/EditResolutionModal';
import SearchAdditionalStaff from './SearchAdditionalStaff';
import LoadingSpinner from '../../components/LoadingSpinner';
import RemoveModal from './Modals/RemoveModal';

// ICONS
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';
import { ReactComponent as NoteIcon } from '../../assets/svg/note-icon.svg';

// TYPES
import { RouteParams } from '../../Interfaces';
import {
	ComplaintFetchComplaint,
	ComplaintStaffCandidatesStaffProfile,
	ComplaintActionsAction,
	ComplaintFetchUserProfile,
	DomainComplaintLoggingType,
	DomainComplaintStatusType,
	ComplaintAddStaffCommand
} from '../../api/models';
import { Resolver } from 'dns';

const ComplaintsDetail: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [complaintDetailFormErrorMessage, setComplaintDetailFormErrorMessage] = React.useState<string>('');

	const [complaintProgressFormErrorMessage, setComplaintProgressFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Getting Id from params
	const params = useParams<RouteParams>();
	const complaintId = params.id;

	// All Modal control state
	const [showResolveModal, setShowResolveModal] = React.useState<boolean>(false);

	const [showEditResolutionModal, setShowEditResolutionModal] = React.useState<boolean>(false);

	const [showReviewResolutionModal, setShowReviewResolutionModal] = React.useState<boolean>(false);

	const [showViewResolutionModal, setShowViewResolutionModal] = React.useState<boolean>(false);

	const [showWidthdrawComplaintModal, setShowWidthdrawComplaintModal] = React.useState<boolean>(false);

	const [showReOpenModal, setShowReOpenModal] = React.useState<boolean>(false);

	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	// Set up data state and initial data
	const initialData: ComplaintFetchComplaint = {
		id: undefined,
		subject: undefined,
		creator: undefined,
		anonymousContact: undefined,
		loggingOnBehalf: undefined,
		categoryDescription: undefined,
		statusDescription: undefined,
		occurrence: undefined,
		location: undefined,
		accused: undefined,
		accusedRelationship: undefined,
		impacted: undefined,
		impactedRelationship: undefined,
		othersInvolved: undefined,
		witnesses: undefined,
		initialComplaint: undefined,
		description: undefined,
		authority: undefined,
		comments: undefined,
		summaryUrl: undefined,
		canAccessSummary: undefined,
		resolver: undefined,

		attachments: [],
		assignees: [],
		familyMembers: [],
		leaders: [],
		resolutionReviewers: []
	};

	// Set up Complaind Detail Data state and initial data
	const [complaintsDetailData, setComplaintsDetailData] = React.useState<ComplaintFetchComplaint>(initialData);

	const getComplaintsDetailsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getComplaintsDetailsData(complaintId);

		if (response.isError) {
			setIsLoading(false);

			setComplaintDetailFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialData } = response;

			setIsLoading(false);

			if (result.assignees === undefined) {
				result.assignees = [];
			}

			setComplaintsDetailData(result);
		}
	};

	// Set up Complaint Progress Data State
	const [complaintProgressData, setComplaintProgressData] = React.useState<ComplaintActionsAction[]>([]);

	const getComplaintsProgress = async () => {
		setIsLoading(true);

		const response = await apibridge.getComplaintsProgress(complaintId);

		if (response.isError) {
			setIsLoading(false);

			setComplaintProgressFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setComplaintProgressData(result);
		}
	};

	// Combine 2 api call to hide and show loading spinner at the same time
	const getComplaintDetailDashboardData = async () => {
		setIsLoading(true);

		await getComplaintsDetailsData();

		await getComplaintsProgress();

		setIsLoading(false);
	};

	React.useEffect(() => {
		getComplaintDetailDashboardData();
	}, []);

	const {
		id,
		identifier,
		subject,
		creator,
		resolver,
		loggingType,
		loggingOnBehalf,
		anonymousContact,
		categoryDescription,
		status,
		statusDescription,
		occurrence,
		location,
		accused,
		accusedRelationship,
		impacted,
		impactedRelationship,
		othersInvolved,
		witnesses,
		initialComplaint,
		description,
		comments,
		authority,
		attachments,
		assignees,
		familyMembers,
		summaryUrl,
		canCreateResolution,
		canEditResolution,
		canViewResolution,
		canReviewResolution,
		canReopen,
		canWithdraw,
		canRemoveFamilyMember,
		canViewActions,
		canAddActions,
		canAddStaff,
		canAccessSummary,
		resolutionReviewers,
		leaders,
		insufficientResolvers
	} = complaintsDetailData;

	// LOGIC TO ADD MORE STAFF
	const initialStaffProfile = {
		id: undefined,
		userId: undefined,
		name: undefined,
		role: undefined,
		profileImage: undefined
	};

	const [staffProfile, setStaffProfile] = React.useState<ComplaintStaffCandidatesStaffProfile>(initialStaffProfile);

	// Toogle Section to add more staff
	const [showAddStaffControl, setShowAddStaffControl] = React.useState<boolean>(false);

	// State to display the staff chosen/clicked
	const [displayClickResult, setDisplayClickResult] =
		React.useState<ComplaintStaffCandidatesStaffProfile>(initialStaffProfile);

	const addStaffClickHandler = (item: ComplaintStaffCandidatesStaffProfile) => {
		if (item) {
			setStaffProfile(item);

			setShowAddStaffControl(true);
		} else {
			setShowAddStaffControl(false);
		}
	};

	const addStaffToComplaint = async () => {
		setIsLoading(true);

		const data: ComplaintAddStaffCommand = {
			id: complaintId,
			profileId: staffProfile.id
		};

		const response = await apibridge.addComplaintResolveStaffMember(data);

		if (response.isError) {
			setIsLoading(false);

			setComplaintDetailFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setStaffProfile(initialStaffProfile);

			setDisplayClickResult(initialStaffProfile);

			setShowAddStaffControl(false);

			getComplaintsDetailsData();
		}
	};

	// LOGIC TO REMOVE FAMILY MEMBER
	const [familyMemberDetail, setFamilyMemberDetail] = React.useState<ComplaintFetchUserProfile>({});

	const removeFamilyMember = async (item: ComplaintFetchUserProfile) => {
		setFamilyMemberDetail(item);

		setShowRemoveModal(true);
	};

	return (
		<div className="page-general-layout complaints-detail-page">
			{!!complaintDetailFormErrorMessage ? (
				<div className="form-validation-error">{complaintDetailFormErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<PageName title={subject} showBack={true} />

								<div className="complaints-detail-general-info">
									<div className="general-info-left">
										<div className="info-col">
											<div className="info-title">Submitted by</div>
											<div className="info-detail">{creator}</div>
										</div>

										<div className="info-col">
											<div className="info-title">Complaint ID</div>
											<div className="info-detail">{identifier}</div>
										</div>

										<div className="info-col">
											<div className="info-title">Category</div>
											<div className="info-detail">{categoryDescription}</div>
										</div>

										<div className="info-col">
											<div className="info-title">Status</div>
											{status !== DomainComplaintStatusType.Closed && (
												<div className="info-detail">{statusDescription}</div>
											)}

											{status === DomainComplaintStatusType.Closed && (
												<div className="info-detail">
													{resolutionReviewers && resolutionReviewers.length > 0 && (
														<>
															<p className="mbtom-8">Resolved by</p>
															<p className="mbtom-8">{resolver}</p>

															{resolutionReviewers
																.filter((item) => item.approved === true)
																.map((item, index) => {
																	return (
																		<p className="mbtom-8" key={item.id}>
																			{item.name}
																			{index !== resolutionReviewers.length - 1 && ','}
																		</p>
																	);
																})}
														</>
													)}
												</div>
											)}
										</div>
									</div>

									<div className="general-info-right">
										{isOrganisationActive && canCreateResolution && (
											<Button className="btn-primary resolve-btn mleft-10" onClick={() => setShowResolveModal(true)}>
												Mark as resolved
											</Button>
										)}

										{isOrganisationActive && canEditResolution && (
											<Button
												className="btn-primary resolve-btn mleft-10"
												onClick={() => setShowEditResolutionModal(true)}
											>
												Edit resolution
											</Button>
										)}

										{isOrganisationActive && canReviewResolution && (
											<Button
												className="btn-primary resolve-btn mleft-10"
												onClick={() => setShowReviewResolutionModal(true)}
											>
												Review resolution
											</Button>
										)}

										{isOrganisationActive && !canReviewResolution && !canEditResolution && canViewResolution && (
											<Button
												className="btn-primary resolve-btn mleft-10"
												onClick={() => setShowViewResolutionModal(true)}
											>
												View resolution
											</Button>
										)}

										{isOrganisationActive && canReopen && (
											<Button
												className="btn-secondary resolve-btn mleft-10 wide"
												onClick={() => setShowReOpenModal(true)}
											>
												Re-open Complaint
											</Button>
										)}

										{isOrganisationActive && canWithdraw && (
											<Button
												className="btn-primary resolve-btn mleft-10 wide"
												onClick={() => setShowWidthdrawComplaintModal(true)}
											>
												Withdraw complaint
											</Button>
										)}
									</div>
								</div>

								<div className="two-col-layout">
									<div className="complaints-detail">
										{isOrganisationActive &&
											(status === DomainComplaintStatusType.Open || status === DomainComplaintStatusType.InProgress) &&
											canAddStaff && (
												<>
													{insufficientResolvers && (
														<div className="complaint-alert-box">
															<div className="box-info">
																<NoteIcon />

																<div className="info-text">
																	<p className="mbtom-8">
																		Insufficient number of members to resolve a complaint. Please add additional staff
																		members.
																	</p>
																</div>
															</div>

															<SearchAdditionalStaff
																isFullWidth={true}
																placeholder="Search for a member"
																onChange={(item: ComplaintStaffCandidatesStaffProfile) => addStaffClickHandler(item)}
																complaintId={complaintId}
																setShowAddStaffControl={setShowAddStaffControl}
																displayClickResult={displayClickResult}
																setDisplayClickResult={setDisplayClickResult}
															/>

															{showAddStaffControl ? (
																<div className="form-button-group align-left">
																	<Button className="btn-primary" onClick={addStaffToComplaint}>
																		Add Staff Member to Complaint
																	</Button>
																</div>
															) : null}
														</div>
													)}
												</>
											)}

										<div className="detail-block">
											<h2 className="detail-heading">
												Submitted{' '}
												{loggingType === DomainComplaintLoggingType.SomeoneElse && loggingOnBehalf
													? 'on behalf of'
													: 'by'}{' '}
											</h2>
											<p className="detail-info">
												{loggingType === DomainComplaintLoggingType.Myself && 'Myself'}

												{loggingType === DomainComplaintLoggingType.SomeoneElse && loggingOnBehalf && (
													<>{loggingOnBehalf}</>
												)}

												{loggingType === DomainComplaintLoggingType.Anonymous && <>Anonymous ({anonymousContact})</>}
											</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Date and time of relevant event</h2>
											<time className="detail-info">
												<DisplayDateTime>{occurrence}</DisplayDateTime>
											</time>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Location of relevant event</h2>
											<p className="detail-info">{location}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Who is the complaint about?</h2>
											<p className="detail-info">{accused}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">What is your relationship to this person(s)?</h2>
											<p className="detail-info">{accusedRelationship}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Name of person(s) impacted</h2>
											<p className="detail-info">{impacted}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">What is your relationship to this person(s)?</h2>
											<p className="detail-info">{impactedRelationship}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Who else was involved in the relevant event?</h2>
											<p className="detail-info">{othersInvolved}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Who witnessed the relevant event?</h2>
											<p className="detail-info">{witnesses}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Is this your first complaint about this matter?</h2>
											<p className="detail-info">{initialComplaint ? 'Yes' : 'No'}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">
												Please detail your complaint including details of relevant event/s.
											</h2>
											<p className="detail-info">{description}</p>
										</div>

										{authority ? (
											<div className="detail-block">
												<h2 className="detail-heading">
													Please provide the name of the authority and the contact details of the person that you spoke
													to at that authority
												</h2>
												<p className="detail-info">{authority}</p>
											</div>
										) : null}

										<div className="detail-block">
											<h2 className="detail-heading">Additional comments</h2>
											<p className="detail-info">{comments}</p>
										</div>

										{attachments && attachments.length > 0 && (
											<div className="detail-block">
												<h2 className="detail-heading">Attachments</h2>

												<ul>
													{attachments.map((item, index) => {
														return (
															<li key={index}>
																<a className="attachments-download-link" href={item.attachmentUrl} download>
																	<span className="link-name">{item.displayFileName}</span>

																	<span className="link-text d-none d-lg-block">Download</span>

																	<DownloadIcon className="link-icon d-block d-lg-none" />
																</a>
															</li>
														);
													})}
												</ul>
											</div>
										)}

										<div className="detail-block">
											<h2 className="detail-heading mbtom-14">Complaint notified to:</h2>

											{((leaders && leaders.length > 0) || (assignees && assignees.length > 0)) && (
												<>
													<h3 className="detail-heading mbtom-14">Leadership Team members</h3>

													<ul className="mbtom-14">
														{leaders &&
															leaders.length > 0 &&
															leaders.map((item, index) => {
																return (
																	<li key={index} className="row mbtom-14">
																		<div className="col-2">
																			<CircleImage fullName={item.name} src={item.profileImage} size={40} />
																		</div>

																		<div className="col-4 people-name">{item.name}</div>

																		<div className="col-4 people-role">{item.role}</div>
																	</li>
																);
															})}

														<>
															{assignees &&
																assignees.length > 0 &&
																assignees.map((item, index) => {
																	return (
																		<li key={index} className="row mbtom-14">
																			<div className="col-2">
																				<CircleImage fullName={item.name} src={item.profileImage} size={40} />
																			</div>

																			<div className="col-4 people-name">{item.name}</div>

																			<div className="col-4 people-role">{item.role}</div>
																		</li>
																	);
																})}
														</>
													</ul>
												</>
											)}

											{familyMembers && familyMembers.length > 0 && (
												<>
													<h3 className="detail-heading mbtom-14">Family members</h3>

													<ul>
														{familyMembers.map((item, index) => {
															return (
																<li key={index} className="row mbtom-14">
																	<div className="col-2">
																		<CircleImage fullName={item.name} src={item.profileImage} size={40} />
																	</div>

																	<div className="col-4 people-name">{item.name}</div>

																	<div className="col-4 people-role">{item.role}</div>

																	<div className="col-2">
																		{canRemoveFamilyMember && isOrganisationActive && (
																			<Button className="btn-text" onClick={() => removeFamilyMember(item)}>
																				Remove
																			</Button>
																		)}
																	</div>
																</li>
															);
														})}
													</ul>
												</>
											)}
										</div>

										{canAccessSummary && summaryUrl && (
											<a className="btn btn-secondary mtop-40 download-btn" href={summaryUrl} download>
												<DownloadIcon className="link-icon" />
												Download Complaint Summary
											</a>
										)}
									</div>

									{canViewActions && (
										<ComplaintsProgress
											complaintId={complaintId}
											canViewActions={canViewActions}
											canAddActions={canAddActions}
											getComplaintsDetailsData={getComplaintsDetailsData}
											complaintProgressData={complaintProgressData}
											getComplaintsProgress={getComplaintsProgress}
											complaintProgressFormErrorMessage={complaintProgressFormErrorMessage}
											isLoading={isLoading}
										></ComplaintsProgress>
									)}

									<GlobalModal isOpen={showResolveModal} setIsOpen={setShowResolveModal} titleText="Mark as resolved">
										<ResolveModal complaintId={id} />
									</GlobalModal>

									<GlobalModal
										isOpen={showEditResolutionModal}
										setIsOpen={setShowEditResolutionModal}
										titleText="Mark as resolved"
									>
										<EditResolutionModal complaintId={id} />
									</GlobalModal>

									<GlobalModal
										isOpen={showReviewResolutionModal}
										setIsOpen={setShowReviewResolutionModal}
										titleText="Review proposed resolution"
									>
										<ReviewResolutionModal complaintId={id} />
									</GlobalModal>

									<GlobalModal
										isOpen={showViewResolutionModal}
										setIsOpen={setShowViewResolutionModal}
										titleText="View proposed resolution"
									>
										<ViewResolutionModal complaintId={id} />
									</GlobalModal>

									<GlobalModal
										isOpen={showWidthdrawComplaintModal}
										setIsOpen={setShowWidthdrawComplaintModal}
										titleText="Withdraw complaint"
									>
										<WithdrawComplaintModal complaintId={id} complaintSubject={subject} />
									</GlobalModal>

									<GlobalModal isOpen={showReOpenModal} setIsOpen={setShowReOpenModal} titleText="Re-open Complaint">
										<ReOpenModal complaintId={id} />
									</GlobalModal>

									<GlobalModal isOpen={showRemoveModal} setIsOpen={setShowRemoveModal} titleText="Remove family member">
										<RemoveModal
											complaintId={complaintId}
											getComplaintsDetailsData={getComplaintsDetailsData}
											familyMemberDetail={familyMemberDetail}
										/>
									</GlobalModal>
								</div>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default ComplaintsDetail;
